import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { FormControl, Validators, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { NgIf, NgSwitch, NgSwitchCase, NgClass } from '@angular/common';

import { LoginService } from '../../services/login.service';
import { simpleTransitions } from '../../shared/animations/simple-transitions';
import { decode } from '../../shared/utils/decode';

import { ErrorDialogComponent } from './dialogs/error.component';
import { TermsConditionsDialogComponent } from './dialogs/terms-conditions.component';
import { PrivacyPolicyDialogComponent } from './dialogs/privacy-policy.component';
import { FaqDialogComponent } from './dialogs/faq.component';
import { InfoDialogComponent } from './dialogs/info.component';
import { PrivacySettingComponent } from './privacy-setting.component';

@Component({
	selector: 'copo-login-legacy',
	templateUrl: './login-legacy.component.html',
	animations: simpleTransitions,
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [NgIf, TranslateModule, ReactiveFormsModule, NgSwitch, NgSwitchCase, NgClass]
})
export class LoginLegacyComponent implements OnDestroy {
	@HostBinding('class.copo-login-legacy') readonly componentClass = true;
	@HostBinding('class.full-height') fullHeight = true;

	submitting = false;
	emailPattern = '^[a-zA-Z0-9._\'-]+@[a-zA-Z0-9.-]{2,}[.][a-zA-Z]{2,}$';

	loginFormGroup = new FormGroup({
		username: new FormControl('', { validators: Validators.required }),
		password: new FormControl('', { validators: Validators.required }),
		rememberMe: new FormControl(false)
	});

	forgotPasswordFormGroup = new FormGroup({
		userEmail: new FormControl('', [Validators.required, Validators.pattern(this.emailPattern)]),
	});

	errorStatus: string | undefined;
	infoStatus: string | undefined;
	state: string | undefined;
	responseType: string | undefined;
	redirectUri: string | undefined;
	clientId: string | undefined;
	codeChallenge: string | undefined;
	codeChallengeMethod: string | undefined;
	displayHelp = false;
	submittingTimeout: number | undefined;
	legacy = true;

	langsDisplayed = false;
	lostPasswordDisplayed = false;
	lostPasswordSubmitting = false;
	lostPasswordOverImg?: string;
	notReferencedOverImg?: string;
	displayLangsTimeout: number | undefined;
	langSub?: Subscription;
	lang: string;
	chapter?: string;
	supplierDisplayed = false;
	termsSupplierDisplayed = false;
	usefulLinksDisplayed = false;
	// eslint-disable-next-line deprecation/deprecation
	dialogueRef: MatDialogRef<PrivacySettingComponent> | undefined;
	private readonly dialogSubscription: Subscription = new Subscription();
	constructor(
		private readonly route: ActivatedRoute,
		private readonly http: HttpClient,
		private readonly translate: TranslateService,
		private readonly loginService: LoginService,
		// eslint-disable-next-line deprecation/deprecation
		public dialog: MatDialog
	) {
		this.initAnalytics();
		this.lang = this.translate.currentLang;
		this.chapter = 'aboutSteelUser';
		this.lostPasswordOverImg = '';
		this.notReferencedOverImg = '';

		const params = <Record<string, string | undefined>> this.route.snapshot.queryParams;
		this.errorStatus = decode(params['error_status']);
		this.state = params['state'];
		this.responseType = params['response_type'];
		this.redirectUri = params['redirect_uri'];
		this.clientId = params['client_id'];
		this.codeChallenge = params['code_challenge'];
		this.codeChallengeMethod = params['code_challenge_method'];
		if ((this.clientId === '23PYeRXT6nXYKUFa' || this.clientId === undefined)
				&& this.readCookie('cookieInfoRead').length === 0) {
			this.openPrivacySettingsDialog();
		}
		if (this.clientId) {
			return;
		}
		this.clientId = '23PYeRXT6nXYKUFa';
		this.responseType = 'code';
		this.state = '/';
	}

	get emailContactUsToSupport() {
		return this.createMailToLink('login-legacy.ask-a-question');
	}

	get emailBecomeMemberToSupport() {
		return this.createMailToLink('login-legacy.not-referenced');
	}

	get challengeUrl() {
		return this.createUrl('/rest/auth/challenge');
	}

	get accessUrl() {
		return this.createUrl('/rest/auth/oauth2');
	}

	get forgotPasswordUrl() {
		return this.loginService.createUrl(
			'/rest/v1/email/forgotPassword'
		);
	}

	get year() {
		return new Date().getFullYear();
	}

	get userEmail() {
		return this.forgotPasswordFormGroup.get('userEmail');
	}

	async onSubmit() {
		if (!this.loginFormGroup.valid) {
			return;
		}
		this.errorStatus = undefined;
		this.submitting = true;
		try {
			const val = <Record<string, string>> this.loginFormGroup.value;
			const body = new URLSearchParams();
			body.set('username', val['username']);
			body.set('password', val['password']);
			body.set('remember_me', val['rememberMe']);
			await firstValueFrom(this.http.post(
				this.challengeUrl.toString(),
				body.toString(),
				{
					headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
				}
			));
			location.replace(this.accessUrl.toString());
			// reenable after 30 seconds (an error probably happened on load)
			this.submittingTimeout = setTimeout(
				() => {
					this.submittingTimeout = undefined;
					this.submitting = false;
				},
				30_000
			);
		} catch (err) {
			// reenable after 3 seconds to prevent spamming
			this.submittingTimeout = setTimeout(
				() => {
					this.submittingTimeout = undefined;
					this.submitting = false;
				},
				3000
			);
			if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
				console.error(err);
				return;
			}
			if (typeof err.error === 'object') {
				this.errorStatus = (<{ status: string }> err.error).status;
				this.errorStatus ||= 'unknown';
				this.openErrorDialog();
			} else {
				console.error(err);
			}
		}
	}

	async onForgotPasswordSubmit() {
		if (!this.forgotPasswordFormGroup.valid) {
			return;
		}
		this.errorStatus = undefined;
		this.lostPasswordSubmitting = true;
		try {
			const val = <Record<string, string>> this.forgotPasswordFormGroup.value;
			const body = new URLSearchParams();
			body.set('userEmail', val['userEmail']);
			body.set('isNewPortal', 'false');
			await firstValueFrom(this.http.post(
				this.forgotPasswordUrl.toString(),
				body.toString(),
				{
					headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
				}
			));
			this.forgotPasswordFormGroup.reset();
			this.infoStatus = 'lost_password_email_sent';
			this.openInfoDialog();
		} catch (err) {
			if (!(err instanceof HttpErrorResponse)) {
				console.error(err);
				return;
			}
			if (typeof err.error === 'object') {
				this.errorStatus = (<{ status: string }> err.error).status;
				this.errorStatus ||= 'unknown';
				this.openErrorDialog();
			} else {
				console.error(err);
			}
		} finally {
			this.lostPasswordDisplayed = false;
			this.lostPasswordSubmitting = false;
		}
	}

	initAnalytics() {
		const gaScript = document.createElement('script');
		gaScript.src = '//www.google-analytics.com/analytics.js';
		gaScript.async = true;
		// eslint-disable-next-line unicorn/prefer-dom-node-append
		document.head.appendChild(gaScript);

		const ga: GoogleAnalytics = window.ga ||= (...args) => {
			(ga.q ||= []).push(args);
		};
		ga.l = Date.now();
		let accountId = 'UA-88236987-1';
		if (document.location.hostname === 'auth.steeluser.com') {
			accountId = 'UA-88236987-2';
		}
		ga('create', accountId, 'auto');
		ga('send', 'pageview');
	}

	writeCookie(name: string, value: string, days: number) {
		const d = new Date();
		d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
		const expires = ';expires='.concat(d.toUTCString());
		const domain = document.location.host.endsWith('.steeluser.com') ? ';domain=.steeluser.com' : '';
		// eslint-disable-next-line unicorn/no-document-cookie
		document.cookie = name.concat('=', value, expires, domain, ';path=/;secure');
	}

	helpToggle(event: Event) {
		this.displayHelp = !this.displayHelp;
		event.preventDefault();
	}

	ngOnDestroy() {
		if (this.submittingTimeout !== undefined) {
			clearTimeout(this.submittingTimeout);
			this.submittingTimeout = undefined;
		}
		if (this.displayLangsTimeout !== undefined) {
			clearTimeout(this.displayLangsTimeout);
			this.displayLangsTimeout = undefined;
		}
		if (this.langSub) {
			this.langSub.unsubscribe();
		}
		this.dialogSubscription.unsubscribe();
	}

	outDivLangs() {
		this.displayLangsTimeout = setTimeout(
			() => {
				this.langsDisplayed = false;
			},
			5000
		);
	}

	inDivLangs() {
		if (this.displayLangsTimeout !== undefined) {
			clearTimeout(this.displayLangsTimeout);
			this.displayLangsTimeout = undefined;
		}
	}

	setLanguage(lang: string) {
		this.langSub = this.translate.use(lang).subscribe(() => {
			this.lang = lang;
			localStorage.setItem('language', lang);
			this.langsDisplayed = !this.langsDisplayed;
		});
	}

	openAmfceTermsConditionsDialog() {
		this.openTermsAndConditionsDialog('amfce');
	}

	openAmdsTermsConditionsDialog() {
		this.openTermsAndConditionsDialog('amds');
	}

	openPrivacyPolicyDialog() {
		this.dialog.open(PrivacyPolicyDialogComponent, {
			width: '1000px',
			maxHeight: '100vh',
			maxWidth: '100vw',
			panelClass: 'legacy-dialog'
		});
	}

	openPrivacySettingsDialog() {
		this.dialogueRef = this.dialog.open(
			PrivacySettingComponent,
			{
				panelClass: 'privacy-modal',
				width: '1000px',
				height: '500px',
				disableClose: true,
				autoFocus: false
			}
		);
	}

	openFaqDialog() {
		this.dialog.open(FaqDialogComponent, {
			width: '1000px',
			maxHeight: '100vh',
			maxWidth: '100vw',
			panelClass: 'legacy-dialog'
		});
	}

	openErrorDialog() {
		this.dialog.open(ErrorDialogComponent, {
			width: '350px',
			panelClass: 'legacy-dialog',
			data: { errorStatus: this.errorStatus }
		});
	}

	openInfoDialog() {
		this.dialog.open(InfoDialogComponent, {
			width: '490px',
			panelClass: 'legacy-dialog',
			data: { infoStatus: this.infoStatus }
		});
	}

	onLostPasswordOut() {
		this.lostPasswordOverImg = '';
	}

	onLostPasswordOver() {
		this.lostPasswordOverImg = '_hover';
	}

	onNotReferencedOut() {
		this.notReferencedOverImg = '';
	}

	onNotReferencedOver() {
		this.notReferencedOverImg = '_hover';
	}

	readCookie(cookieName: string) {
		const nameEQ = cookieName.concat('=');
		const ca = document.cookie.split(';');
		for (let c of ca) {
			while (c.startsWith(' ')) {
				c = c.slice(1);
			}
			if (c.startsWith(nameEQ)) {
				return c.slice(nameEQ.length, c.length);
			}
		}
		return '';
	}

	private createUrl(path: string) {
		return this.loginService.createUrl(
			path,
			this.state,
			this.responseType,
			this.redirectUri,
			this.clientId,
			this.codeChallenge,
			this.codeChallengeMethod
		);
	}

	private openTermsAndConditionsDialog(type: string) {
		this.dialog.open(TermsConditionsDialogComponent, {
			width: '350px',
			panelClass: 'legacy-dialog',
			data: type
		});
	}

	private createMailToLink(subjectTr: string) {
		return `mailto:ebusiness.support@arcelormittal.com?subject=${
			encodeURIComponent(<string> this.translate.instant(subjectTr))
		}`;
	}
}
